import { isSafeCssColor, isSafeCssSpacing } from './appearanceSettings';

// Style par défaut de TOUS les titres de la plateforme (AnimationSettings →
// General → "Default Title Styles"). Un style local passé explicitement à un
// composant garde toujours la priorité — voir getDefaultTitleStyle() plus bas.
export const DEFAULT_TITLE_STYLE_DEFAULTS = Object.freeze({
  enabled: false,
  responsive: true,
  autoScaleFont: true,
  autoWrapText: true,
  autoEllipsis: false,
  useClamp: true,

  fontFamily: 'inherit',
  fontWeight: '700',
  fontStyle: 'normal',
  letterSpacing: '-0.02em',
  wordSpacing: 'normal',
  lineHeight: '1.2',
  textTransform: 'none',
  textDecoration: 'none',
  textAlign: 'left',
  whiteSpace: 'normal',
  wordBreak: 'normal',
  overflowWrap: 'anywhere',
  textShadow: '',
  opacity: 1,

  fontSizeMin: '1.35rem',
  fontSizeDefault: '1.75rem',
  // Limite globale des titres utilisant le style par défaut.
  fontSizeMax: '2rem',
  desktopFontSize: '2rem',
  laptopFontSize: '1.75rem',
  tabletFontSize: '1.5rem',
  mobileFontSize: '1.25rem',

  textColor: '#1f2937',
  hoverColor: '',
  activeColor: '',
  disabledColor: 'rgba(31, 41, 55, 0.45)',
  textGradientEnabled: false,
  textGradientColor1: '#007399',
  textGradientColor2: '#00a4d8',
  gradientDirection: '135deg',

  backgroundColor: '',
  backgroundGradientEnabled: false,
  backgroundGradientColor1: '#f5f7fa',
  backgroundGradientColor2: '#c3cfe2',
  backgroundOpacity: 1,

  marginTop: '',
  marginBottom: '',
  paddingTop: '',
  paddingBottom: '',

  borderRadius: '',
  borderEnabled: false,
  borderColor: 'rgba(0, 0, 0, 0.08)',
  borderWidth: '1px',
  outlineEnabled: false,
  outlineColor: 'rgba(0, 115, 153, 0.4)',
  outlineWidth: '1px',
  boxShadowEnabled: false,
  boxShadowColor: 'rgba(15, 23, 42, 0.25)',
  animation: 'none',
  transition: 'all 0.3s ease',
});

const LEGACY_DEFAULT_TITLE_FONT_SIZE_MAX_VALUES = new Set(['2.5rem', '2.25rem']);

// Migre uniquement les anciennes valeurs par défaut. Une valeur personnalisée
// par l'administrateur reste intacte, tandis que les anciens réglages adoptent
// automatiquement la nouvelle limite de 2rem.
export const normalizeDefaultTitleStyleSettings = (rawSettings = {}) => {
  const settings = { ...rawSettings };
  if (LEGACY_DEFAULT_TITLE_FONT_SIZE_MAX_VALUES.has(String(settings.fontSizeMax || '').trim())) {
    settings.fontSizeMax = DEFAULT_TITLE_STYLE_DEFAULTS.fontSizeMax;
  }
  return settings;
};

const ANIMATION_CLASS_MAP = {
  none: '',
  fadeIn: 'elakay-default-title-fade-in',
  slideIn: 'elakay-default-title-slide-in',
  scaleIn: 'elakay-default-title-scale-in',
};

const clampOpacity = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(1, Math.max(0, number)) : fallback;
};

const buildFontSize = (settings) => {
  if (!settings.responsive || !settings.autoScaleFont) {
    return settings.fontSizeDefault || DEFAULT_TITLE_STYLE_DEFAULTS.fontSizeDefault;
  }
  if (settings.useClamp) {
    const min = settings.fontSizeMin || DEFAULT_TITLE_STYLE_DEFAULTS.fontSizeMin;
    const max = settings.fontSizeMax || DEFAULT_TITLE_STYLE_DEFAULTS.fontSizeMax;
    return `clamp(${min}, calc(${min} + 1vw), ${max})`;
  }
  return 'var(--elakay-default-title-size)';
};

/**
 * getDefaultTitleStyle(defaultTitleStyleSettings, localOverrides)
 *
 * Renvoie { style, className, variables } prêt à être spread sur un titre.
 * enabled=false (défaut) => les défauts codés en dur d'origine s'appliquent
 * (aucun changement de comportement). enabled=true => les personnalisations
 * admin remplacent ces défauts. Dans tous les cas, toute clé présente dans
 * localOverrides (non undefined) écrase la valeur correspondante en dernier.
 */
export const getDefaultTitleStyle = (rawSettings, localOverrides = {}) => {
  // DEFAULT_TITLE_STYLE_DEFAULTS reproduit le look codé en dur des
  // composants avant ce système (fontFamily inherit, textColor #1f2937,
  // fontWeight 700, ...). "enabled" ne fait que décider si les
  // personnalisations admin (rawSettings) remplacent ces défauts — quand
  // enabled=false, le rendu reste identique à avant l'ajout de ce système.
  const adminSettings = rawSettings?.enabled
    ? normalizeDefaultTitleStyleSettings(rawSettings)
    : {};
  const settings = { ...DEFAULT_TITLE_STYLE_DEFAULTS, ...adminSettings };
  const overrides = localOverrides || {};

  const style = {
    fontFamily: settings.fontFamily,
    fontWeight: settings.fontWeight,
    fontStyle: settings.fontStyle,
    letterSpacing: settings.letterSpacing,
    wordSpacing: settings.wordSpacing,
    lineHeight: settings.lineHeight,
    textTransform: settings.textTransform,
    textDecoration: settings.textDecoration,
    textAlign: settings.textAlign,
    opacity: clampOpacity(settings.opacity, 1),
    fontSize: buildFontSize(settings),
    transition: settings.transition,
  };

  if (settings.textShadow) style.textShadow = settings.textShadow;

  if (settings.autoWrapText) {
    style.whiteSpace = 'normal';
    style.overflowWrap = settings.overflowWrap;
    style.wordBreak = settings.wordBreak;
    style.maxWidth = '100%';
  } else {
    style.whiteSpace = 'nowrap';
  }
  if (settings.autoEllipsis) {
    style.overflow = 'hidden';
    style.textOverflow = 'ellipsis';
  }

  if (settings.textGradientEnabled
    && isSafeCssColor(settings.textGradientColor1)
    && isSafeCssColor(settings.textGradientColor2)) {
    style.backgroundImage = `linear-gradient(${settings.gradientDirection}, ${settings.textGradientColor1}, ${settings.textGradientColor2})`;
    style.WebkitBackgroundClip = 'text';
    style.backgroundClip = 'text';
    style.WebkitTextFillColor = 'transparent';
    style.color = 'transparent';
  } else if (isSafeCssColor(settings.textColor)) {
    style.color = settings.textColor;
  }

  if (settings.backgroundGradientEnabled
    && isSafeCssColor(settings.backgroundGradientColor1)
    && isSafeCssColor(settings.backgroundGradientColor2)) {
    style.background = `linear-gradient(${settings.gradientDirection}, ${settings.backgroundGradientColor1}, ${settings.backgroundGradientColor2})`;
  } else if (settings.backgroundColor && isSafeCssColor(settings.backgroundColor)) {
    style.backgroundColor = settings.backgroundColor;
  }
  if (style.background || style.backgroundColor) {
    style.opacity = clampOpacity(settings.backgroundOpacity, style.opacity ?? 1);
  }

  if (settings.marginTop && isSafeCssSpacing(settings.marginTop)) style.marginTop = settings.marginTop;
  if (settings.marginBottom && isSafeCssSpacing(settings.marginBottom)) style.marginBottom = settings.marginBottom;
  if (settings.paddingTop && isSafeCssSpacing(settings.paddingTop)) style.paddingTop = settings.paddingTop;
  if (settings.paddingBottom && isSafeCssSpacing(settings.paddingBottom)) style.paddingBottom = settings.paddingBottom;
  if (settings.borderRadius && isSafeCssSpacing(settings.borderRadius)) style.borderRadius = settings.borderRadius;

  if (settings.borderEnabled) {
    const borderColor = isSafeCssColor(settings.borderColor) ? settings.borderColor : DEFAULT_TITLE_STYLE_DEFAULTS.borderColor;
    style.border = `${settings.borderWidth} solid ${borderColor}`;
  }
  if (settings.outlineEnabled) {
    const outlineColor = isSafeCssColor(settings.outlineColor) ? settings.outlineColor : DEFAULT_TITLE_STYLE_DEFAULTS.outlineColor;
    style.outline = `${settings.outlineWidth} solid ${outlineColor}`;
  }
  if (settings.boxShadowEnabled) {
    const shadowColor = isSafeCssColor(settings.boxShadowColor) ? settings.boxShadowColor : DEFAULT_TITLE_STYLE_DEFAULTS.boxShadowColor;
    style.boxShadow = `0 8px 24px ${shadowColor}`;
  }

  const variables = {
    '--elakay-default-title-hover-color': settings.hoverColor || settings.textColor,
    '--elakay-default-title-active-color': settings.activeColor || settings.hoverColor || settings.textColor,
    '--elakay-default-title-disabled-color': settings.disabledColor,
  };

  const animationClass = ANIMATION_CLASS_MAP[settings.animation] || '';
  const className = ['elakay-default-title', animationClass].filter(Boolean).join(' ');

  // Les clés explicitement fournies par l'appelant gagnent toujours.
  Object.keys(overrides).forEach((key) => {
    if (overrides[key] !== undefined) style[key] = overrides[key];
  });

  return { style, className, variables };
};

export const useDefaultTitleStyle = (infoSource, localOverrides = {}) => (
  getDefaultTitleStyle(infoSource?.allSettings?.defaultTitleStyle, localOverrides)
);

/**
 * applyDefaultTitleStyleVars(defaultTitleStyleSettings)
 *
 * Écrit les CSS custom properties globales (hover/active/disabled + tailles
 * par appareil en mode "breakpoints") sur <html>, et bascule la classe
 * elakay-title-fixed-breakpoints selon useClamp. Appelé depuis Routes.js à
 * chaque changement de allSettings.defaultTitleStyle.
 */
export const applyDefaultTitleStyleVars = (rawSettings) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const adminSettings = rawSettings?.enabled
    ? normalizeDefaultTitleStyleSettings(rawSettings)
    : {};
  const settings = { ...DEFAULT_TITLE_STYLE_DEFAULTS, ...adminSettings };

  root.style.setProperty('--elakay-default-title-hover-color', settings.hoverColor || settings.textColor);
  root.style.setProperty('--elakay-default-title-active-color', settings.activeColor || settings.hoverColor || settings.textColor);
  root.style.setProperty('--elakay-default-title-disabled-color', settings.disabledColor);

  root.style.setProperty('--elakay-default-title-size-desktop', settings.desktopFontSize);
  root.style.setProperty('--elakay-default-title-size-laptop', settings.laptopFontSize);
  root.style.setProperty('--elakay-default-title-size-tablet', settings.tabletFontSize);
  root.style.setProperty('--elakay-default-title-size-mobile', settings.mobileFontSize);

  const fixedBreakpoints = settings.responsive && settings.autoScaleFont && !settings.useClamp;
  root.classList.toggle('elakay-title-fixed-breakpoints', fixedBreakpoints);
};
